import React, { useState, useEffect } from 'react';
import SiteLinks from './SiteLinks';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';

const TRENDING_LIMIT = 8;
const SUGGESTIONS_LIMIT = 5;

const RightSidebar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [trending, setTrending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(!!user);
  const [showAllTrending, setShowAllTrending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchTrending = async () => {
      try {
        const { data } = await axios.get(`/api/hashtags/trending?limit=${TRENDING_LIMIT}`);
        if (!cancelled) setTrending(data.hashtags || []);
      } catch (err) {
        if (!cancelled) setTrending([]);
      } finally {
        if (!cancelled) setLoadingTrending(false);
      }
    };
    fetchTrending();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return;
    }
    let cancelled = false;
    const fetchSuggestions = async () => {
      try {
        const { data } = await axios.get(`/api/follows/suggestions?limit=${SUGGESTIONS_LIMIT}`);
        if (!cancelled) setSuggestions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    };
    fetchSuggestions();
    return () => { cancelled = true; };
  }, [user]);

  const handleFollow = async (userId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.post(`/api/follows/${userId}`);
      setSuggestions((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      console.error('Follow error:', err);
    }
  };

  return (
    <aside className="hidden lg:flex flex-col w-[310px] xl:w-[340px] flex-shrink-0 sticky top-0 self-start max-h-screen overflow-y-auto no-scrollbar py-6 px-6 ltr:border-l rtl:border-r border-gray-100 dark:border-zinc-800/80">
      <section className="mb-6 p-3.5 rounded-2xl bg-zinc-50/70 dark:bg-zinc-900/40 border border-zinc-100 dark:border-zinc-800/80">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <i className="fas fa-fire text-amber-500 text-xs" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
              {t('sidebar.trending')}
            </h2>
          </div>
          <Link
            to="/explore"
            className="text-[11px] font-semibold text-amber-500 hover:text-amber-600 dark:hover:text-amber-400"
          >
            Explore
          </Link>
        </div>

        {loadingTrending ? (
          <div className="space-y-2 py-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-6 rounded bg-zinc-200 dark:bg-zinc-800 animate-pulse" />
            ))}
          </div>
        ) : trending.length === 0 ? (
          <p className="text-xs text-zinc-400 py-1">{t('sidebar.noTrending')}</p>
        ) : (
          <div className="space-y-1">
            {(showAllTrending ? trending : trending.slice(0, 4)).map((tag, idx) => (
              <Link
                key={tag.id}
                to={`/hashtag/${encodeURIComponent(tag.name)}`}
                className="flex items-center justify-between py-1.5 px-2 rounded-xl hover:bg-zinc-200/50 dark:hover:bg-zinc-800/60 transition-colors group"
                dir="ltr"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-bold text-zinc-400 group-hover:text-amber-500 transition-colors w-4">
                    #{idx + 1}
                  </span>
                  <span className="text-xs font-semibold text-zinc-800 dark:text-zinc-200 group-hover:text-amber-500 transition-colors truncate">
                    #{tag.name}
                  </span>
                </div>
                {tag.postCount != null && (
                  <span className="text-[10px] text-zinc-400 flex-shrink-0 ms-1">
                    {tag.postCount}
                  </span>
                )}
              </Link>
            ))}

            {trending.length > 4 && (
              <button
                type="button"
                onClick={() => setShowAllTrending((prev) => !prev)}
                className="w-full pt-1 text-center text-[11px] font-medium text-amber-500 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
              >
                {showAllTrending ? 'Show less' : `Show ${trending.length - 4} more`}
              </button>
            )}
          </div>
        )}
      </section>

      {user && (
        <section>
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-3">
            {t('sidebar.whoToFollow')}
          </h2>
          {loadingSuggestions ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
          ) : suggestions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('sidebar.noSuggestions')}</p>
          ) : (
            <ul className="space-y-3.5">
              {suggestions.map((s) => (
                <li key={s.id} className="flex items-center gap-3">
                  <Link
                    to={`/profile/${s.handle}`}
                    className="flex items-center gap-3 min-w-0 flex-1"
                  >
                    {s.profilePicture ? (
                      <img
                        src={getMediaUrl(s.profilePicture)}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-user text-gray-500 dark:text-gray-400 text-sm" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-gray-900 dark:text-gray-100 truncate block">
                        <bdi>{s.username}</bdi>
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 truncate block">
                        <bdi dir="ltr">@{s.handle}</bdi>
                      </span>
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => handleFollow(s.id, e)}
                    className="flex-shrink-0 px-4 py-1.5 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    {t('sidebar.follow')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      <SiteLinks className="px-4 pb-6" />
    </aside>
  );
};

export default RightSidebar;
